import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { partnerSetupService } from '../../../services/partnerProfileService';

const SetupTab = ({ partner, rawPartner }) => {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    contract_renewal_date: '',
    assigned_account_manager: '',
    tier_justification: '',
    onboarding_documents_uploaded: false,
    onboarding_contacts_added: false,
    onboarding_products_mapped: false,
    onboarding_training_assigned: false
  });

  useEffect(() => {
    if (partner?.id) loadConfig();
  }, [partner?.id]);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const data = await partnerSetupService?.getByPartner(partner?.id);
      if (data) {
        setConfig(data);
        setForm({
          contract_renewal_date: data?.contract_renewal_date || '',
          assigned_account_manager: data?.assigned_account_manager || '',
          tier_justification: data?.tier_justification || '',
          onboarding_documents_uploaded: data?.onboarding_documents_uploaded || false,
          onboarding_contacts_added: data?.onboarding_contacts_added || false,
          onboarding_products_mapped: data?.onboarding_products_mapped || false,
          onboarding_training_assigned: data?.onboarding_training_assigned || false
        });
      }
    } catch (err) {
      console.error('Error loading setup config:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await partnerSetupService?.upsert(partner?.id, form);
      await loadConfig();
    } catch (err) {
      console.error('Error saving setup config:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleCheckToggle = async (field) => {
    const newVal = !form?.[field];
    const newForm = { ...form, [field]: newVal };
    setForm(newForm);
    try {
      await partnerSetupService?.upsert(partner?.id, newForm);
    } catch (err) {
      console.error('Error updating checklist:', err);
    }
  };

  const checklistItems = [
    { key: 'onboarding_documents_uploaded', label: 'Documents Uploaded', desc: 'NDA, contracts, certifications uploaded', icon: 'FileText' },
    { key: 'onboarding_contacts_added', label: 'Contacts Added', desc: 'Primary and key contacts registered', icon: 'Users' },
    { key: 'onboarding_products_mapped', label: 'Products Mapped', desc: 'Portfolio products and services assigned', icon: 'Package' },
    { key: 'onboarding_training_assigned', label: 'Training Assigned', desc: 'Enablement training tracks assigned', icon: 'GraduationCap' }
  ];

  const completedCount = checklistItems?.filter(item => form?.[item?.key])?.length;
  const completionPct = Math.round((completedCount / checklistItems?.length) * 100);

  const quickLinks = [
    { label: 'Portfolio Mapping', icon: 'Map', path: '/portfolio-mapping-interface', color: 'from-blue-500 to-cyan-500' },
    { label: 'Enablement Center', icon: 'GraduationCap', path: '/partner-enablement-center', color: 'from-green-500 to-emerald-500' },
    { label: 'Lifecycle Control', icon: 'RefreshCw', path: '/partner-lifecycle-control', color: 'from-purple-500 to-pink-500' },
    { label: 'All Partners', icon: 'Building2', path: '/all-partners', color: 'from-orange-500 to-amber-500' }
  ];

  if (loading) {
    return (
      <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-elevation-lg border border-slate-200/60 p-32">
        <div className="flex items-center justify-center py-48">
          <div className="w-8 h-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-24">
      {/* Onboarding Checklist */}
      <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-elevation-lg border border-slate-200/60 p-32">
        <div className="flex items-center gap-12 mb-24">
          <div className="p-12 bg-gradient-to-br from-primary to-secondary rounded-xl">
            <Icon name="CheckSquare" size={24} className="text-white" />
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-slate-900">Onboarding Checklist</h2>
            <p className="text-sm text-slate-600">{completedCount} of {checklistItems?.length} steps completed</p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-primary">{completionPct}%</div>
            <div className="text-xs text-slate-500">Complete</div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="h-3 bg-slate-100 rounded-full overflow-hidden mb-28">
          <div
            className="h-full bg-gradient-to-r from-primary to-secondary rounded-full transition-all duration-500"
            style={{ width: `${completionPct}%` }}
          ></div>
        </div>

        <div className="space-y-16">
          {checklistItems?.map((item) => (
            <div
              key={item?.key}
              onClick={() => handleCheckToggle(item?.key)}
              className={`flex items-center gap-16 p-20 rounded-xl border-2 cursor-pointer transition-all ${
                form?.[item?.key]
                  ? 'border-success/30 bg-success/5' :'border-slate-200 bg-white hover:border-primary/30 hover:bg-primary/5'
              }`}
            >
              <div className={`w-10 h-10 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                form?.[item?.key] ? 'bg-success border-success' : 'border-slate-300'
              }`}>
                {form?.[item?.key] && <Icon name="Check" size={14} className="text-white" />}
              </div>
              <div className={`p-10 rounded-lg ${form?.[item?.key] ? 'bg-success/10' : 'bg-slate-100'}`}>
                <Icon name={item?.icon} size={20} className={form?.[item?.key] ? 'text-success' : 'text-slate-500'} />
              </div>
              <div className="flex-1">
                <div className={`font-semibold ${form?.[item?.key] ? 'text-success line-through' : 'text-slate-900'}`}>
                  {item?.label}
                </div>
                <div className="text-sm text-slate-500">{item?.desc}</div>
              </div>
              {form?.[item?.key] && (
                <span className="px-12 py-4 bg-success/10 text-success rounded-full text-xs font-semibold">Done</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Partnership Configuration */}
      <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-elevation-lg border border-slate-200/60 p-32">
        <div className="flex items-center gap-12 mb-24">
          <div className="p-12 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl">
            <Icon name="Settings" size={24} className="text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Partnership Configuration</h2>
            <p className="text-sm text-slate-600">Contract details and account management</p>
          </div>
        </div>

        <div className="space-y-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-20">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-8">Contract Renewal Date</label>
              <input
                type="date"
                value={form?.contract_renewal_date}
                onChange={(e) => setForm(prev => ({ ...prev, contract_renewal_date: e?.target?.value }))}
                className="w-full px-16 py-12 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-8">Assigned Account Manager</label>
              <input
                type="text"
                value={form?.assigned_account_manager}
                onChange={(e) => setForm(prev => ({ ...prev, assigned_account_manager: e?.target?.value }))}
                placeholder="e.g. Jane Smith"
                className="w-full px-16 py-12 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-8">Tier Justification Notes</label>
            <textarea
              value={form?.tier_justification}
              onChange={(e) => setForm(prev => ({ ...prev, tier_justification: e?.target?.value }))}
              placeholder="Explain why this partner is assigned their current tier..."
              rows={4}
              className="w-full px-16 py-12 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 resize-none"
            />
          </div>
          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-24 py-12 bg-gradient-to-r from-primary to-secondary text-white rounded-xl font-semibold text-sm shadow-md hover:shadow-lg transition-all disabled:opacity-60 flex items-center gap-8"
            >
              {saving ? (
                <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Saving...</>
              ) : (
                <><Icon name="Save" size={16} /> Save Configuration</>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-elevation-lg border border-slate-200/60 p-32">
        <div className="flex items-center gap-12 mb-24">
          <div className="p-12 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl">
            <Icon name="ExternalLink" size={24} className="text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Quick Links</h2>
            <p className="text-sm text-slate-600">Navigate to related partner management areas</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-16">
          {quickLinks?.map((link) => (
            <button
              key={link?.path}
              onClick={() => navigate(link?.path)}
              className="flex flex-col items-center gap-12 p-20 bg-white rounded-xl border border-slate-200 hover:shadow-elevation-md hover:border-primary/30 transition-all group"
            >
              <div className={`p-14 rounded-xl bg-gradient-to-br ${link?.color} shadow-md group-hover:shadow-lg transition-all`}>
                <Icon name={link?.icon} size={24} className="text-white" />
              </div>
              <span className="text-sm font-semibold text-slate-700 text-center">{link?.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SetupTab;
